import { Component, computed, inject, OnInit, signal } from '@angular/core';
import { TestUser } from '../../api/types';
import { Router, RouterLink } from '@angular/router';
import { Location } from '@angular/common';
import { ColumnDescription, TableComponent } from '../components/table';
import {
  SynAlertComponent,
  SynBreadcrumbComponent,
  SynBreadcrumbItemComponent,
  SynButtonComponent, SynIconComponent, SynTooltipComponent,
} from '@synergy-design-system/angular';
import { StateService } from '../../api/state';
import { getUserData } from '../../api/auth';

@Component({
  selector: 'app-test-user-manager',
  template: `
    <syn-breadcrumb>
      <syn-breadcrumb-item routerLink="" i18n="@@home">Home</syn-breadcrumb-item>
      <syn-breadcrumb-item routerLink="/customers" i18n="@@test-users">Test Users</syn-breadcrumb-item>
    </syn-breadcrumb>

    <div class="flex-row gap-1">
      <h1 i18n="@@test-users" data-e2e="heading-test-users">Test Users</h1>
      <syn-button class="margin-vert-center" size="small" routerLink="./new"
                  [disabled]="!hasAccess()"
                  data-e2e="btn-create-test-user">
        <syn-icon name="add_fill" slot="prefix" />
        <span i18n="@@create-new-test-user">Create New Test User</span>
      </syn-button>
    </div>

    @if (!hasAccess()) {
      <syn-alert variant="danger" [open]="true" class="margin-bottom-1">
        <span i18n="@@error-no-test-user-access">
          You do not have access to the test user manager. You can request access via the following link:
        </span>&nbsp;<a href="https://userservice.sick.com/saw/ess/offeringPage/20232">SMAX</a>
        <syn-icon slot="icon" name="info_fill" />
      </syn-alert>
    }

    <ng-template #rowTemplate let-value>
      <!-- Enable type checking in the template -->
      @let user = identity(value);
      <tr (click)="openUser(user)">
        <td>{{ user.username }}</td>
        <td>{{ user.email }}</td>
        <td>{{ user.firstName }}</td>
        <td>{{ user.lastName }}</td>
        <td>
          <div class="flex-row gap-05 align-center">
            {{ user.lastLogin }}
            @if (isInactive(user)) {
              <syn-tooltip style="" i18n-content="@@test-user-inactive-warning"
                           content="The test user has been inactive for a long time. Either log in with the test user or delete them.">
                <syn-icon
                  style="color: var(--syn-color-warning-500); font-size: var(--syn-font-size-large); min-width: 32px"
                  name="info_fill" />
              </syn-tooltip>
            }
          </div>
        </td>
        <td>
          <syn-icon [name]="user.enabled ? 'check_fill' : 'close_fill'" style="font-size: var(--syn-font-size-large)" />
        </td>
      </tr>
    </ng-template>

    <app-table
      [columns]="columns"
      [values]="users()"
      [rowTemplate]="rowTemplate"
      [pageSize]="20"
      [clickable]="true"
      [loading]="loading()"
      [(searchTextField)]="query"
    />
  `,
  styles: [
    `
    `,
  ],
  imports: [
    RouterLink,
    SynButtonComponent,
    SynIconComponent,
    SynBreadcrumbComponent,
    SynBreadcrumbItemComponent,
    TableComponent,
    SynAlertComponent,
    SynTooltipComponent,
  ],
})
export class TestUserManagerComponent implements OnInit {
  state = inject(StateService);
  router = inject(Router);
  location = inject(Location);
  now = new Date();

  users = this.state.testUsers;
  loading = computed(() => {
    return this.hasAccess() && this.state.testUsersLoading();
  });

  query = signal('');
  hasAccess = signal(false);

  columns: ColumnDescription<TestUser>[] = [
    {
      label: $localize`:@@username:Username`,
      getField: (account) => account.username,
      style: 'width: 20%',
    },
    {
      label: $localize`:@@email:Email`,
      getField: (account) => account.email ?? '',
      style: 'width: 20%',
    },
    {
      label: $localize`:@@first-name:First Name`,
      getField: (account) => account.firstName,
      style: 'width: 20%',
    },
    {
      label: $localize`:@@last-name:Last Name`,
      getField: (account) => account.lastName,
      style: 'width: 20%',
    },
    {
      label: $localize`:@@last-login:Last Login`,
      getField: (account) => account.lastLogin ?? '',
      style: 'width: 15%',
    },
    {
      label: $localize`:@@enabled:Enabled`,
      getField: (account) => account.enabled ? '✔' : '✖',
      style: 'width: 5%',
    },
  ];

  ngOnInit() {
    const roles = getUserData().roles;
    const isManager = roles.includes('test_user_manager');
    this.hasAccess.set(isManager);
    if (isManager) {
      this.state.queryTestUsers();
    }
  }

  openUser(user: TestUser) {
    void this.router.navigate(['test-users', user.id, 'properties']);
  }

  isInactive(user: TestUser): boolean {
    if (!user.lastLogin) {
      return true;
    }
    const lastLogin = Date.parse(user.lastLogin);
    const sinceLastLoginMs = this.now.getTime() - lastLogin;
    const sinceLastLoginDays = sinceLastLoginMs / 1000 / 60 / 60 / 24.0;
    return sinceLastLoginDays > 182;
  }

  identity(user: TestUser) {
    return user;
  }
}
